import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  Actions,
  createEffect,
  ofType
} from "@ngrx/effects";
import { initializeApp } from "../actions/app/initialize";
import {
  catchError,
  finalize,
  filter,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom
} from "rxjs";
import { Store } from "@ngrx/store";
import { AuthService } from "src/app/features/auth/auth.service";
import { initializedWithNoToken } from "../actions/app/initialized-with-no-token";
import { initializeTokenValue } from "../actions/app/initialize-token-value";
import { UserProfileService } from "src/app/common/services/api/userprofile/userprofile.service";
import { rejectUserDetails } from "../actions/user/reject-user-details";
import { resolveUserDetails } from "../actions/user/resolve-user-details";
import { generateAvatarFromName } from "src/app/common/utils/generateAvatarFromName";
import { AccountService } from "src/app/common/services/api/account/account.service";
import { rejectCurrentProject } from "../actions/account/reject-current-project";
import { resolveCurrentProject } from "../actions/account/resolve-current-project";
import { initializedWithToken } from "../actions/app/initialized-with-token";
import { initializedWithError } from "../actions/app/initialized-with-error";
import { signOut } from "../actions/user/signout";
import { onboardUser } from "../actions/user/onboard-user";
import { selectProject } from "../actions/account/select-project";
import { favoriteProject } from "../actions/account/favorite-project";
import { removeFavoriteProject } from "../actions/account/remove-favorite-project";
import { resolveAssociatedProjects } from "../actions/account/resolve-associated-projects";
import { rejectAssociatedProjects } from "../actions/account/reject-associated-projects";
import { fetchAssociatedProjects } from "../actions/account/fetch-associated-projects";
import { fetchFavoriteProjects } from "../actions/account/fetch-favorite-projects";
import { resolveFavoriteProjects } from "../actions/account/resolve-favorite-projects";
import { rejectFavoriteProjects } from "../actions/account/reject-favorite-projects";
import { createProject } from "../actions/account/create-project";
import { updateProject } from "../actions/account/update-project";
import { GlobalState } from "../types/global-state";
import { openLoader } from "../actions/loader/open-loader";
import { closeLoader } from "../actions/loader/close-loader";
import { TemplateLayoutStore } from "src/app/common/lib/template-layout-renderer/template-layout.store";

@Injectable()
export class AppEffects {
  private actions$ = inject(Actions);

  private store = inject(Store);

  private authService = inject(AuthService);

  private userProfileService = inject(UserProfileService);

  private accountService = inject(AccountService);

  private router = inject(Router);

  private templateLayoutStore = inject(TemplateLayoutStore);

  initialize$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(initializeApp),
      map(() => {
        // if token exist, fetch all info,
        // otherwise just set initialized to true
        const token = this.authService.getStoredAuthToken();
        const decodedValue = this.authService.decodeAuthToken(token);

        if (!token || !decodedValue?.userId) {
          return initializedWithNoToken();
        }

        return initializeTokenValue({
          token,
          storedAccountId: this.authService.getAccountId(),
          userId: Number(decodedValue.userId),
          accountId: this.authService.getAccountId() ?? Number(decodedValue.accountId),
          accountIds: decodedValue.accountIds?.split(',').map((id) => Number(id)) ?? [],
          accountType: decodedValue.accountType,
          role: decodedValue.role,
        });
      })
    );
  });

  initializeTokenValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(initializeTokenValue),
      withLatestFrom(this.store.select((state) => state.appState.userDetails)),
      filter(([, user]) => !!user.data.id && user.isLoading),
      switchMap(([, user]) =>
        this.userProfileService.getUserProfile(user.data.id).pipe(
          map((res) => {
            if (!res.isSuccess) {
              return rejectUserDetails({ error: res.message });
            }

            return resolveUserDetails({
              user: {
                ...res.data,
                avatar: generateAvatarFromName(res.data.firstName + ' ' + res.data.lastName),
              },
            });
          }),
          catchError((error) => {
            return of(rejectUserDetails({ error: error.message }));
          })
        )
      )
    );
  });

  associatedAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(initializeTokenValue, fetchAssociatedProjects),
      withLatestFrom(this.store.select((state) => state.appState.associatedAccounts)),
      filter(([, associatedAccounts]) => associatedAccounts.isLoading),
      switchMap(() =>
        this.userProfileService.getAssociatedProjects().pipe(
          map((res) => {
            return resolveAssociatedProjects({ projects: res.data });
          }),
          catchError((error) =>
            of(rejectAssociatedProjects({ error: error.message }))
          )
        )
      )
    );
  });

  onboardUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(onboardUser),
      map(({ token: newToken }) => {

        this.authService.setTokenNoInit(newToken);

        const decodedValue = this.authService.decodeAuthToken(newToken);
        if (!newToken || !decodedValue?.userId) {
          return initializedWithNoToken();
        }

        return initializeTokenValue({
          token: newToken,
          userId: Number(decodedValue.userId),
          accountId: Number(decodedValue.accountId), // temporary?
          accountType: decodedValue.accountType,
          storedAccountId: this.authService.getAccountId(),
          accountIds: decodedValue.accountIds?.split(',').map((id) => Number(id)) ?? [],
          role: decodedValue.role,
        });
      })
    );
  });

  navigateToRootOnProjectSwitch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(selectProject),
        tap(() => this.router.navigate(['/'], { queryParams: { refresh: true } }))
      ),
    { dispatch: false }
  );

  fetchCurrentProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(resolveUserDetails, selectProject),
      withLatestFrom(this.store.select((state) => state.appState)),
      filter(([, appState]) => !!appState.userDetails.data.accountId
        && (
          appState.project.isLoading
          && appState.client.isLoading
        )
      ),
      switchMap(([, appState]) =>
        this.accountService.getParentAccount(
          appState.userDetails.data.accountId
        ).pipe(
          map((res) => {
            if (!res.isSuccess) {
              return rejectCurrentProject({ error: res.message });
            }

            this.authService.setAccountId(appState.userDetails.data.accountId);

            return resolveCurrentProject({
              client: res.data.parentDto ?? res.data.currentAccountDto,
              project: res.data.currentAccountDto,
            });
          }),
          catchError((error) =>
            of(rejectCurrentProject({ error: error.message }))
          )
        )
      )
    );
  });

  /**
   * TODO: add logic for checking if permissions are locked and loaded
   */
  resolveAuthenticatedUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        resolveUserDetails,
        resolveCurrentProject,
        resolveAssociatedProjects,
        resolveFavoriteProjects,
        rejectCurrentProject,
        rejectUserDetails,
        rejectFavoriteProjects
      ),
      withLatestFrom(this.store.select((state: GlobalState) => state.appState)),
      filter(([, appState]) =>
        !appState.userDetails.isLoading
        && !appState.project.isLoading
        && !appState.client.isLoading
        && !appState.associatedAccounts.isLoading
        && !appState.favoriteProjects.isLoading
      ),
      map(([, appState]) => {
        if (
          appState.userDetails.isError
          || appState.project.isError
          || appState.client.isError
          || appState.associatedAccounts.isError
          || appState.favoriteProjects.isError
        ) {
          return initializedWithError();
        }

        return initializedWithToken();
      })
    );
  });

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        signOut
      ),
      tap(() => {
        window.location.href = '/login';
      })
    ), { dispatch: false }
  );

  primeTemplateLayoutsOnProjectResolve$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resolveCurrentProject),
        withLatestFrom(this.store.select((state: GlobalState) => state.appState.accountType)),
        tap(([{ project }, accountType]) => {
          this.templateLayoutStore.invalidate();
          if (project?.accountId) {
            this.templateLayoutStore.loadForActiveAccount(project.accountId, accountType);
          }
        })
      ),
    { dispatch: false }
  );

  // initializedWithToken$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(initializedWithToken),
  //     withLatestFrom(this.store.select((state) => state.appState)),
  //     tap(([, appState]) => {
  //       const guestOnlyRoutes = [
  //         '/login',
  //         '/auth/callback',
  //         '/invites',
  //       ];

  //       const unAuthRoute = guestOnlyRoutes.some((path) => this.location.path().startsWith(path));

  //       console.log(unAuthRoute);

  //       if (
  //         appState.isAuthenticated
  //         && unAuthRoute
  //       ) {
  //         // window.location.reload();
  //       }

  //     })
  //   ),
  // { dispatch: false }
  // );

  createProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createProject),
      switchMap(({ payload }) => {
        this.store.dispatch(
          openLoader({
            message: 'Saving account...',
            customImage: payload.accountLogoBase64,
          })
        );
        return this.accountService.createAccount(payload).pipe(
          map(() => fetchAssociatedProjects()),
          catchError((error) =>
            of(rejectAssociatedProjects({ error: error.message }))
          ),
          finalize(() => this.store.dispatch(closeLoader()))
        );
      })
    );
  });

  updateProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateProject),
      withLatestFrom(this.store.select((state) => state.appState.associatedAccounts.data)),
      switchMap(([{ payload }]) => {
        this.store.dispatch(
          openLoader({ message: 'Updating account...' })
        );
        return this.accountService.updateAccount(payload).pipe(
          map(() => fetchAssociatedProjects()),
          catchError((error) =>
            of(rejectAssociatedProjects({ error: error.message }))
          ),
          finalize(() => this.store.dispatch(closeLoader()))
        );
      })
    );
  });

  favoriteProjects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(initializeTokenValue, fetchFavoriteProjects),
      withLatestFrom(this.store.select((state) => state.appState.favoriteProjects)),
      filter(([, favoriteProjects]) => favoriteProjects.isLoading),
      switchMap(() =>
        this.userProfileService.getFavoriteProjects().pipe(
          map((res) => resolveFavoriteProjects({ projects: res.data })),
          catchError((error) =>
            of(rejectFavoriteProjects({ error: error.message }))
          )
        )
      )
    );
  });

  favoriteProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(favoriteProject),
      withLatestFrom(this.store.select((state) => state.appState.userDetails.data?.id)),
      filter((entry): entry is [ReturnType<typeof favoriteProject>, number] => entry[1] !== null),
      switchMap(([{ accountId }, userId]) => {
        this.store.dispatch(openLoader({ message: 'Adding favorite...' }));
        return this.userProfileService.addFavoriteProject(accountId, userId).pipe(
          map((res) =>
            res.isSuccess
              ? fetchFavoriteProjects()
              : rejectFavoriteProjects({ error: res.message })
          ),
          catchError((error) =>
            of(rejectFavoriteProjects({ error: error.message }))
          ),
          finalize(() => this.store.dispatch(closeLoader()))
        );
      })
    );
  });

  removeFavoriteProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(removeFavoriteProject),
      switchMap((payload) => {
        this.store.dispatch(openLoader({ message: 'Removing favorite...' }));
        return this.userProfileService.deleteFavoriteProject(payload).pipe(
          map((res) =>
            res.isSuccess
              ? fetchFavoriteProjects()
              : rejectFavoriteProjects({ error: res.message })
          ),
          catchError((error) =>
            of(rejectFavoriteProjects({ error: error.message }))
          ),
          finalize(() => this.store.dispatch(closeLoader()))
        );
      })
    );
  });
}
